/* External */
import {
  memo, useCallback, useEffect, useRef, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';
import { FormattedMessage } from 'react-intl';
import { Redirect } from 'react-router';
import { useHistory } from 'react-router-dom';

/* Ott-common */
import Loading from '@dtvgo/loading';

/* Other */
import { getJsonImage } from 'src/theme/images';

/* Components */
import CheckMarkIcon from '~/assets/theme/svg/ico-checkmark-circle.svg?react';
import Error from './Error';
import PinNumber from './PinNumber';
import QrCode from '../../../components/QrCode';
import Steps from './Steps';
import Header from '../Header';
import FocusableButton from '../../../components/FocusableButton';

/* Constants */
import {
  REPORT_GA,
  MILLISECONDS_TO_DISPLAY_LOGIN_SUCCESS,
  STORAGE_COUNTRY,
} from '../../../utils/constants';

/* Providers */
import { useCountry, useIsBrazil } from '../../../providers/ConfigProvider';
import { useLoading } from '../../../providers/LoadingProvider';

/* Services */
import { gaService, gaServiceError } from '../../../services/gaService';

/* Utils */
import { getLink, Routes } from '../../../utils/router';
import useLoginDevice from './utils/useLoginDevice';
import useRegistrationCode from './utils/useRegistrationCode';
import useQuery from '../../../hooks/useQuery';
import { useGtmLocalContext } from '../../../providers/GtmLocalProvider';
import { GTM_CATEGORIES, GTM_EVENT_NAMES, GTM_SUBCATEGORIES } from '../../../utils/googleTags';
import { WEB_URL } from '../../../utils/env';
import getHeaderElementClass from './utils/getHeaderElementClass';
import getContentElementClass from './utils/getContentElementClass';
import getContentMessageId from './utils/getContentMessageId';
import { isCreateAccountActiveForCountry } from '../../../utils/profiles';

/* Styles */
import './PinLogin.scss';

function PinLogin({ realFocusKey, setFocus }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const RETRY_BUTTON_FOCUS_KEY = `${realFocusKey}-RETRY`;
  const CREATE_ACCOUNT_BUTTON_FOCUS_KEY = `${realFocusKey}-create-account-button`;
  const country = useCountry();
  const isBrazil = useIsBrazil();
  const { showLoading } = useLoading();
  const checkMarkIcon = getJsonImage('icon-checkmark-circle');
  const [displayLoginSuccess, setLoginSuccess] = useState(undefined);
  const isLoginFlowCreateAccountActive = isCreateAccountActiveForCountry(country);

  const {
    registrationCode,
    deviceCode,
    showError,
    errorText,
    resetNumberOfAttempts,
  } = useRegistrationCode(country);
  const isLoggedIn = useLoginDevice(registrationCode, country, deviceCode);
  const { commonTaggingEvent } = useGtmLocalContext();
  const tagPinLoginRef = useRef(true);
  const tagsMountRef = useRef(true);

  useEffect(() => {
    if (country) {
      localStorage.setItem(STORAGE_COUNTRY, country);
    }
  }, [country]);

  useEffect(() => {
    if (tagsMountRef.current && country) {
      commonTaggingEvent({
        category: GTM_CATEGORIES.LOGIN_DATA,
        subCategory: GTM_SUBCATEGORIES.LOGIN.ACTIVATION_OPTIONS,
        eventName: GTM_EVENT_NAMES.PAGE_VIEW,
      });
      tagsMountRef.current = false;
    }
  }, [commonTaggingEvent, country]);

  useEffect(() => {
    if (tagPinLoginRef.current && isLoggedIn) {
      commonTaggingEvent({
        category: GTM_CATEGORIES.LOGIN_DATA,
        subCategory: GTM_SUBCATEGORIES.LOGIN.ACTIVATION_OPTIONS,
        eventName: GTM_EVENT_NAMES.LOGIN.PIN_LOGIN_SUCCESS,
      });
      tagPinLoginRef.current = false;
    }
  }, [commonTaggingEvent, isLoggedIn]);

  const handleErrorEnterPress = useCallback(() => {
    resetNumberOfAttempts();
    gaServiceError.sendEvent({
      action: REPORT_GA.GA_EVENT_ACTION_BUTTON_CLICK,
      label: REPORT_GA.GA_EVENT_LABEL_NA,
      category: REPORT_GA.GA_ERROR_EVENT_CATEGORY_PIN_REATTEMPTS,
    });
  }, [resetNumberOfAttempts]);

  const headerElementClass = getHeaderElementClass(isLoginFlowCreateAccountActive);
  const contentElementClass = getContentElementClass(isLoginFlowCreateAccountActive);
  const contentMessageId = getContentMessageId(isLoginFlowCreateAccountActive);

  const handleCreateAccount = useCallback(() => {
    commonTaggingEvent({
      category: GTM_CATEGORIES.LOGIN_DATA,
      subCategory: GTM_SUBCATEGORIES.LOGIN.ACTIVATION_OPTIONS,
      eventName: GTM_EVENT_NAMES.ACCOUNT.CREATE_ACCOUNT,
    });
    history.push(getLink(Routes.Public.CreateAccount));
  }, [commonTaggingEvent, history]);

  useEffect(() => {
    gaService.sendEvent({
      action: REPORT_GA.GA_EVENT_ACTION_INTENTION,
      label: REPORT_GA.GA_EVENT_LABEL_NA,
      category: REPORT_GA.GA_EVENT_CATEGORY_DEVICE_ACTIVATION,
    });
  }, []);

  useEffect(() => {
    if (showError) {
      setFocus(RETRY_BUTTON_FOCUS_KEY);
      gaServiceError.sendEvent({
        action: REPORT_GA.GA_ERROR_EVENT_ACTION_ERROR_RECEIVED,
        label: errorText,
        category: REPORT_GA.GA_ERROR_EVENT_CATEGORY_PIN_REATTEMPTS,
      });
    } else {
      gaService.sendEvent({
        action: REPORT_GA.GA_EVENT_ACTION_INTENTION,
        label: REPORT_GA.GA_EVENT_LABEL_NA,
        category: REPORT_GA.GA_EVENT_CATEGORY_DEVICE_ACTIVATION,
      });
    }
  }, [RETRY_BUTTON_FOCUS_KEY, errorText, setFocus, showError]);

  useEffect(() => {
    let successMessageTimer;
    if (displayLoginSuccess) {
      successMessageTimer = setTimeout(() => {
        setLoginSuccess(false);
      }, MILLISECONDS_TO_DISPLAY_LOGIN_SUCCESS);
    }

    if (displayLoginSuccess === false) {
      showLoading();
    }

    return () => clearTimeout(successMessageTimer);
  }, [displayLoginSuccess, showLoading]);

  useEffect(() => {
    if (isLoggedIn) {
      setLoginSuccess(true);
      dispatch({ type: 'loginSuccess' });
    }
  }, [isLoggedIn, setLoginSuccess, dispatch]);

  useEffect(() => {
    if (isLoginFlowCreateAccountActive && !showError) {
      setFocus(CREATE_ACCOUNT_BUTTON_FOCUS_KEY);
    }
  }, [
    setFocus,
    showError,
    isLoginFlowCreateAccountActive,
    CREATE_ACCOUNT_BUTTON_FOCUS_KEY,
  ]);

  const { redirectTo = getLink(Routes.Private) } = useQuery();

  if (isLoggedIn && displayLoginSuccess === false) return <Redirect to={redirectTo} />;

  return (
    <section className="dtv-html5-c-pin-login">
      <header className={headerElementClass.header}>
        <Header hiddenRightLogo />

        {!isLoggedIn && (
          <h1 className={headerElementClass.h1}>
            <FormattedMessage id={contentMessageId.pageTitle} />
          </h1>
        )}
      </header>

      {displayLoginSuccess ? (
        <div className="dtv-html5-c-pin-login__content--success">
          {checkMarkIcon ? (
            <img src={checkMarkIcon} alt="check mark icon" />
          ) : (
            <CheckMarkIcon />
          )}
          <h2 className="dtv-html5-c-pin-login__content-title">
            <FormattedMessage id="pinLogin.success.message" />
          </h2>
        </div>
      ) : (
        <>
          <div className={contentElementClass.content}>
            <div className={contentElementClass.container}>
              <h2 className={contentElementClass.title}>
                <FormattedMessage id={contentMessageId.pinNumberTitle} />
              </h2>

              <Steps
                isCreateAccountActive={isLoginFlowCreateAccountActive}
                className={showError
                  ? 'dtv-html5-c-pin-login__steps--dimmed'
                  : undefined}
              />

              {showError && (
                <Error
                  focusKeyButton={RETRY_BUTTON_FOCUS_KEY}
                  onEnterPress={handleErrorEnterPress}
                />
              )}

              {registrationCode ? (
                <PinNumber registrationCode={registrationCode} />
              ) : (
                <div className="dtv-html5-c-pin-login__loading">
                  {!showError && <Loading />}
                </div>
              )}
            </div>

            <div className={contentElementClass.container}>
              <h2 className={contentElementClass.title}>
                <FormattedMessage id={contentMessageId.qrCodeTitle} />
              </h2>

              <p className={contentElementClass.qrCodeDescription}>
                <FormattedMessage id={contentMessageId.qrCodeDescription} />
              </p>

              {registrationCode ? (
                <QrCode
                  data={`${WEB_URL}/${isBrazil ? 'ativar' : 'activar'}-tv?pinCode=${registrationCode}`}
                  className={contentElementClass.qrCode}
                />
              ) : (
                <div className="dtv-html5-c-pin-login__qr-loading">
                  {!showError && <Loading />}
                </div>
              )}
            </div>
          </div>
          {isLoginFlowCreateAccountActive && (
            <div className="dtv-html5-c-pin-login-flow-create-account__footer">
              <p className="dtv-html5-c-pin-login-flow-create-account__footer-text">
                <FormattedMessage id="pinLogin.flowCreateAccount.footerTitle" />
              </p>
              <FocusableButton
                onEnterPress={handleCreateAccount}
                focusKey={CREATE_ACCOUNT_BUTTON_FOCUS_KEY}
                className="dtv-html5-c-pin-login-flow-create-account__footer-button"
              >
                <FormattedMessage id="pinLogin.flowCreateAccount.buttonText" />
              </FocusableButton>
            </div>
          )}
        </>
      )}
    </section>
  );
}

PinLogin.propTypes = {
  realFocusKey: PropTypes.string.isRequired,
  setFocus: PropTypes.func.isRequired,
};

export default memo(withFocusable()(memo(PinLogin)));
